<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required, requiredIf } from '@vuelidate/validators';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { TemplatePreview } from 'dashboard/components-next/template-preview';
import { PLATFORMS } from 'dashboard/services/TemplateConstants';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { formatTemplateLanguage } from './templateUtils';
import {
  TEMPLATE_BUTTON_TYPES,
  TEMPLATE_CATEGORIES,
  TEMPLATE_HEADER_FORMATS,
  TEMPLATE_LANGUAGES,
  TEMPLATE_PARAMETER_FORMATS,
  buildPreviewTemplate,
  buildPreviewVariables,
  buildTemplatePayload,
  createTemplateFormState,
  extractNamedParameters,
  extractPositionalParameters,
  isButtonValid,
  parseBodyExamples,
} from './templateCreationUtils';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
  initialInboxId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['created']);

const TEMPLATE_NAME_PATTERN = /^[a-z0-9_]+$/;
let buttonSequence = 0;

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const isSubmitting = ref(false);
const state = reactive(
  createTemplateFormState(props.inboxes, props.initialInboxId)
);

const allTemplateText = computed(() => [
  state.body,
  state.headerEnabled && state.headerFormat === 'TEXT' ? state.headerText : '',
]);

const namedParameterNames = computed(() =>
  extractNamedParameters(...allTemplateText.value)
);
const bodyPositionalParameterNames = computed(() =>
  extractPositionalParameters(state.body)
);
const headerPositionalParameterNames = computed(() =>
  extractPositionalParameters(state.headerText)
);
const positionalParameterNames = computed(() =>
  extractPositionalParameters(...allTemplateText.value)
);

const v$ = useVuelidate(
  {
    inboxId: {
      required: helpers.withMessage(
        () => t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.INBOX'),
        required
      ),
    },
    name: {
      required: helpers.withMessage(
        () => t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.NAME'),
        required
      ),
      pattern: helpers.withMessage(
        () => t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.NAME_FORMAT'),
        value => TEMPLATE_NAME_PATTERN.test(value)
      ),
    },
    language: {
      required: helpers.withMessage(
        () => t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.LANGUAGE'),
        required
      ),
    },
    category: {
      required: helpers.withMessage(
        () => t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.CATEGORY'),
        required
      ),
    },
    body: {
      required: helpers.withMessage(
        () => t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.BODY'),
        required
      ),
    },
    bodyExamples: {
      required: helpers.withMessage(
        () => t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.BODY_EXAMPLES'),
        requiredIf(
          computed(() => bodyPositionalParameterNames.value.length > 0)
        )
      ),
    },
  },
  state
);

const inboxOptions = computed(() =>
  props.inboxes.map(inbox => ({ value: inbox.id, label: inbox.name }))
);
const categoryOptions = computed(() => {
  const labels = {
    UTILITY: t('WHATSAPP_TEMPLATE_MGMT.CREATE.CATEGORIES.UTILITY'),
    MARKETING: t('WHATSAPP_TEMPLATE_MGMT.CREATE.CATEGORIES.MARKETING'),
    AUTHENTICATION: t(
      'WHATSAPP_TEMPLATE_MGMT.CREATE.CATEGORIES.AUTHENTICATION'
    ),
  };
  return TEMPLATE_CATEGORIES.map(category => ({
    value: category,
    label: labels[category],
  }));
});
const languageOptions = computed(() =>
  TEMPLATE_LANGUAGES.map(language => ({
    value: language,
    label: formatTemplateLanguage(language),
  }))
);
const parameterFormatOptions = computed(() => {
  const labels = {
    POSITIONAL: t('WHATSAPP_TEMPLATE_MGMT.CREATE.PARAMETER_FORMATS.POSITIONAL'),
    NAMED: t('WHATSAPP_TEMPLATE_MGMT.CREATE.PARAMETER_FORMATS.NAMED'),
  };
  return TEMPLATE_PARAMETER_FORMATS.map(format => ({
    value: format,
    label: labels[format],
  }));
});
const headerFormatOptions = computed(() => {
  const labels = {
    TEXT: t('WHATSAPP_TEMPLATE_MGMT.CREATE.HEADER_FORMATS.TEXT'),
    IMAGE: t('WHATSAPP_TEMPLATE_MGMT.CREATE.HEADER_FORMATS.IMAGE'),
    VIDEO: t('WHATSAPP_TEMPLATE_MGMT.CREATE.HEADER_FORMATS.VIDEO'),
    DOCUMENT: t('WHATSAPP_TEMPLATE_MGMT.CREATE.HEADER_FORMATS.DOCUMENT'),
    LOCATION: t('WHATSAPP_TEMPLATE_MGMT.CREATE.HEADER_FORMATS.LOCATION'),
  };
  return TEMPLATE_HEADER_FORMATS.map(format => ({
    value: format,
    label: labels[format],
  }));
});
const buttonTypeOptions = computed(() => {
  const labels = {
    URL: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTON_TYPES.URL'),
    PHONE_NUMBER: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTON_TYPES.PHONE_NUMBER'),
    QUICK_REPLY: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTON_TYPES.QUICK_REPLY'),
    COPY_CODE: t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTON_TYPES.COPY_CODE'),
  };
  return TEMPLATE_BUTTON_TYPES.map(type => ({
    value: type,
    label: labels[type],
  }));
});

const previewCopy = computed(() => ({
  name: t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.NAME'),
  body: t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.BODY'),
  header: t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.HEADER'),
  button: t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.BUTTON'),
}));
const previewTemplate = computed(() =>
  buildPreviewTemplate(state, previewCopy.value)
);
const previewVariables = computed(() => buildPreviewVariables(state));
const hasNonTextHeader = computed(
  () => state.headerEnabled && state.headerFormat !== 'TEXT'
);

const bodyExamplesError = computed(() => {
  if (state.parameterFormat !== 'POSITIONAL') return '';
  const expectedCount = bodyPositionalParameterNames.value.length;
  const actualCount = parseBodyExamples(state.bodyExamples).length;
  if (expectedCount && actualCount !== expectedCount) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.BODY_EXAMPLES_COUNT', {
      count: expectedCount,
    });
  }
  return '';
});

const parameterFormatError = computed(() => {
  if (
    state.parameterFormat === 'POSITIONAL' &&
    namedParameterNames.value.length
  ) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.POSITIONAL_FORMAT');
  }
  if (
    state.parameterFormat === 'NAMED' &&
    positionalParameterNames.value.length
  ) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.NAMED_FORMAT');
  }
  return '';
});

const namedParametersError = computed(() => {
  if (state.parameterFormat !== 'NAMED') return '';
  if (
    state.namedParameters.some(
      parameter => !String(parameter.value || '').trim()
    )
  ) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.NAMED_PARAMETERS');
  }
  return '';
});

const headerTextError = computed(() => {
  if (
    !state.headerEnabled ||
    state.headerFormat !== 'TEXT' ||
    state.headerText.trim()
  ) {
    return '';
  }
  return t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.HEADER_TEXT');
});

const headerExampleError = computed(() => {
  if (
    !state.headerEnabled ||
    state.headerFormat !== 'TEXT' ||
    state.parameterFormat !== 'POSITIONAL' ||
    !headerPositionalParameterNames.value.length ||
    state.headerExample.trim()
  ) {
    return '';
  }
  return t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.HEADER_EXAMPLE');
});

const headerError = computed(() => {
  if (!state.headerEnabled) return '';
  if (headerTextError.value) return headerTextError.value;
  if (headerExampleError.value) return headerExampleError.value;
  if (
    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(state.headerFormat) &&
    !state.headerHandle.trim()
  ) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.HEADER_HANDLE');
  }
  if (
    state.headerFormat === 'LOCATION' &&
    (state.headerLatitude === '' || state.headerLongitude === '')
  ) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.HEADER_LOCATION');
  }
  return '';
});

const buttonsError = computed(() => {
  if (state.buttons.some(button => !isButtonValid(button))) {
    return t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.BUTTONS');
  }
  return '';
});

const isFormValid = computed(
  () =>
    !v$.value.$invalid &&
    !bodyExamplesError.value &&
    !parameterFormatError.value &&
    !namedParametersError.value &&
    !headerError.value &&
    !buttonsError.value
);
const isSubmitDisabled = computed(
  () => isSubmitting.value || !state.inboxId || !isFormValid.value
);

const fieldError = field =>
  v$.value[field].$error ? v$.value[field].$errors[0]?.$message : '';

const addButton = () => {
  state.buttons.push({
    id: (buttonSequence += 1),
    type: 'URL',
    text: '',
    url: '',
    phone_number: '',
    example: '',
  });
};

const removeButton = index => {
  state.buttons.splice(index, 1);
};

const resetForm = () => {
  Object.assign(
    state,
    createTemplateFormState(props.inboxes, props.initialInboxId)
  );
  v$.value.$reset();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const open = () => {
  if (!props.inboxes.length) {
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERRORS.NO_INBOXES'));
    return;
  }

  resetForm();
  dialogRef.value?.open();
};

const handleSubmit = async () => {
  const isBaseFormValid = await v$.value.$validate();
  if (!isBaseFormValid || !isFormValid.value) return;

  isSubmitting.value = true;
  try {
    const response = await store.dispatch('inboxes/createMessageTemplate', {
      inboxId: state.inboxId,
      template: buildTemplatePayload(state),
    });

    useAlert(t('WHATSAPP_TEMPLATE_MGMT.CREATE.SUCCESS'));
    emit('created', {
      inboxId: state.inboxId,
      template: response?.template || response,
    });
    closeDialog();
  } catch (error) {
    const errorMessage = parseAPIErrorResponse(error);
    useAlert(
      typeof errorMessage === 'string'
        ? errorMessage
        : t('WHATSAPP_TEMPLATE_MGMT.CREATE.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  namedParameterNames,
  names => {
    const existingValues = Object.fromEntries(
      state.namedParameters.map(parameter => [parameter.name, parameter.value])
    );
    state.namedParameters = names.map(name => ({
      name,
      value: existingValues[name] || '',
    }));
  },
  { immediate: true }
);

watch(
  () => props.inboxes,
  inboxes => {
    if (!inboxes.some(inbox => inbox.id === state.inboxId)) {
      state.inboxId = inboxes[0]?.id || null;
    }
  },
  { deep: true }
);

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.TITLE')"
    :description="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @confirm="handleSubmit"
    @close="resetForm"
  >
    <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
      <div class="flex flex-col gap-5">
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-1">
            <label
              for="whatsapp-template-inbox"
              class="mb-0.5 text-sm font-medium text-n-slate-12"
            >
              {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.INBOX') }}
            </label>
            <Select
              id="whatsapp-template-inbox"
              v-model="state.inboxId"
              class="!w-full [&>select]:w-full"
              :options="inboxOptions"
              :error="fieldError('inboxId')"
              :aria-label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.INBOX')"
            />
            <p v-if="fieldError('inboxId')" class="mt-1 text-xs text-n-ruby-9">
              {{ fieldError('inboxId') }}
            </p>
          </div>

          <Input
            id="whatsapp-template-name"
            v-model="state.name"
            :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.NAME')"
            :placeholder="
              $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.NAME_PLACEHOLDER')
            "
            :message="fieldError('name')"
            :message-type="fieldError('name') ? 'error' : 'info'"
            maxlength="512"
          />

          <div class="flex flex-col gap-1">
            <label
              for="whatsapp-template-language"
              class="mb-0.5 text-sm font-medium text-n-slate-12"
            >
              {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.LANGUAGE') }}
            </label>
            <Select
              id="whatsapp-template-language"
              v-model="state.language"
              class="!w-full [&>select]:w-full"
              :options="languageOptions"
              :placeholder="
                $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.LANGUAGE_PLACEHOLDER')
              "
              :error="fieldError('language')"
              :aria-label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.LANGUAGE')"
            />
            <p v-if="fieldError('language')" class="mt-1 text-xs text-n-ruby-9">
              {{ fieldError('language') }}
            </p>
          </div>

          <div class="flex flex-col gap-1">
            <label
              for="whatsapp-template-category"
              class="mb-0.5 text-sm font-medium text-n-slate-12"
            >
              {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.CATEGORY') }}
            </label>
            <Select
              id="whatsapp-template-category"
              v-model="state.category"
              class="!w-full [&>select]:w-full"
              :options="categoryOptions"
              :error="fieldError('category')"
              :aria-label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.CATEGORY')"
            />
            <p v-if="fieldError('category')" class="mt-1 text-xs text-n-ruby-9">
              {{ fieldError('category') }}
            </p>
          </div>

          <div class="flex flex-col gap-1 sm:col-span-2">
            <label
              for="whatsapp-template-parameter-format"
              class="mb-0.5 text-sm font-medium text-n-slate-12"
            >
              {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.PARAMETER_FORMAT') }}
            </label>
            <Select
              id="whatsapp-template-parameter-format"
              v-model="state.parameterFormat"
              class="!w-full [&>select]:w-full"
              :options="parameterFormatOptions"
              :aria-label="
                $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.PARAMETER_FORMAT')
              "
            />
            <p v-if="parameterFormatError" class="mt-1 text-xs text-n-ruby-9">
              {{ parameterFormatError }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.HELP.PARAMETER_FORMAT') }}
            </p>
          </div>
        </div>

        <TextArea
          id="whatsapp-template-body"
          v-model="state.body"
          :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BODY')"
          :placeholder="
            $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BODY_PLACEHOLDER')
          "
          :max-length="1024"
          show-character-count
          :message="fieldError('body')"
          :message-type="fieldError('body') ? 'error' : 'info'"
        />

        <div
          v-if="state.parameterFormat === 'POSITIONAL'"
          class="flex flex-col gap-1"
        >
          <Input
            id="whatsapp-template-body-examples"
            v-model="state.bodyExamples"
            :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BODY_EXAMPLES')"
            :placeholder="
              $t(
                'WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BODY_EXAMPLES_PLACEHOLDER'
              )
            "
            :message="bodyExamplesError || fieldError('bodyExamples')"
            :message-type="
              bodyExamplesError || fieldError('bodyExamples') ? 'error' : 'info'
            "
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.HELP.BODY_EXAMPLES') }}
          </p>
        </div>

        <div v-else class="flex flex-col gap-3">
          <div>
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.NAMED_PARAMETERS') }}
            </h3>
            <p class="text-xs text-n-slate-11">
              {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.HELP.NAMED_PARAMETERS') }}
            </p>
          </div>
          <div
            v-if="!state.namedParameters.length"
            class="rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11"
          >
            {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.HELP.NO_NAMED_PARAMETERS') }}
          </div>
          <Input
            v-for="parameter in state.namedParameters"
            :key="parameter.name"
            v-model="parameter.value"
            :label="
              $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.PARAMETER_VALUE', {
                name: parameter.name,
              })
            "
            :message="namedParametersError"
            :message-type="namedParametersError ? 'error' : 'info'"
          />
        </div>

        <section
          class="flex flex-col gap-3 rounded-xl border border-n-weak p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER') }}
              </h3>
              <p class="text-xs text-n-slate-11">
                {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.HELP.HEADER') }}
              </p>
            </div>
            <Switch v-model="state.headerEnabled" />
          </div>

          <div v-if="state.headerEnabled" class="flex flex-col gap-3">
            <div class="flex flex-col gap-1">
              <label
                for="whatsapp-template-header-format"
                class="mb-0.5 text-sm font-medium text-n-slate-12"
              >
                {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER_FORMAT') }}
              </label>
              <Select
                id="whatsapp-template-header-format"
                v-model="state.headerFormat"
                class="!w-full [&>select]:w-full"
                :options="headerFormatOptions"
                :aria-label="
                  $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER_FORMAT')
                "
              />
            </div>

            <Input
              v-if="state.headerFormat === 'TEXT'"
              id="whatsapp-template-header-text"
              v-model="state.headerText"
              :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER_TEXT')"
              :message="headerTextError"
              :message-type="headerTextError ? 'error' : 'info'"
            />
            <Input
              v-else-if="state.headerFormat === 'LOCATION'"
              id="whatsapp-template-header-latitude"
              v-model="state.headerLatitude"
              type="number"
              :label="
                $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER_LATITUDE')
              "
              :message="headerError"
              :message-type="headerError ? 'error' : 'info'"
            />
            <Input
              v-if="state.headerFormat === 'LOCATION'"
              id="whatsapp-template-header-longitude"
              v-model="state.headerLongitude"
              type="number"
              :label="
                $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER_LONGITUDE')
              "
              :message="headerError"
              :message-type="headerError ? 'error' : 'info'"
            />
            <Input
              v-else-if="state.headerFormat !== 'TEXT'"
              id="whatsapp-template-header-handle"
              v-model="state.headerHandle"
              :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER_HANDLE')"
              :placeholder="
                $t(
                  'WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER_HANDLE_PLACEHOLDER'
                )
              "
              :message="headerError"
              :message-type="headerError ? 'error' : 'info'"
            />
            <Input
              v-if="state.headerFormat === 'TEXT'"
              id="whatsapp-template-header-example"
              v-model="state.headerExample"
              :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER_EXAMPLE')"
              :placeholder="
                $t(
                  'WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.HEADER_EXAMPLE_PLACEHOLDER'
                )
              "
              :message="headerExampleError"
              :message-type="headerExampleError ? 'error' : 'info'"
            />
            <p
              v-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(state.headerFormat)"
              class="text-xs text-n-slate-11"
            >
              {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.HELP.HEADER_HANDLE') }}
            </p>
          </div>
        </section>

        <TextArea
          id="whatsapp-template-footer"
          v-model="state.footer"
          :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.FOOTER')"
          :placeholder="
            $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.FOOTER_PLACEHOLDER')
          "
          :max-length="60"
          show-character-count
        />

        <section
          class="flex flex-col gap-3 rounded-xl border border-n-weak p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTONS') }}
              </h3>
              <p class="text-xs text-n-slate-11">
                {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.HELP.BUTTONS') }}
              </p>
            </div>
            <Button
              type="button"
              variant="faded"
              color="slate"
              size="sm"
              :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.ADD')"
              icon="i-lucide-plus"
              @click="addButton"
            />
          </div>

          <p v-if="buttonsError" class="text-xs text-n-ruby-9">
            {{ buttonsError }}
          </p>

          <div
            v-for="(button, index) in state.buttons"
            :key="button.id"
            class="flex flex-col gap-3 rounded-lg bg-n-alpha-2 p-3"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_NUMBER', {
                    index: index + 1,
                  })
                }}
              </span>
              <Button
                type="button"
                variant="ghost"
                color="slate"
                size="sm"
                icon="i-lucide-trash-2"
                :aria-label="
                  $t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.REMOVE', {
                    index: index + 1,
                  })
                "
                @click="removeButton(index)"
              />
            </div>

            <div class="grid gap-3 sm:grid-cols-2">
              <div class="flex flex-col gap-1">
                <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                  {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_TYPE') }}
                </label>
                <Select
                  v-model="button.type"
                  class="!w-full [&>select]:w-full"
                  :options="buttonTypeOptions"
                  :aria-label="
                    $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_TYPE')
                  "
                />
              </div>
              <Input
                v-model="button.text"
                :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_TEXT')"
              />
            </div>

            <Input
              v-if="button.type === 'URL'"
              v-model="button.url"
              type="text"
              :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_URL')"
              :placeholder="
                $t(
                  'WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_URL_PLACEHOLDER'
                )
              "
            />
            <Input
              v-if="button.type === 'PHONE_NUMBER'"
              v-model="button.phone_number"
              :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_PHONE')"
              :placeholder="
                $t(
                  'WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_PHONE_PLACEHOLDER'
                )
              "
            />
            <Input
              v-if="['URL', 'COPY_CODE'].includes(button.type)"
              v-model="button.example"
              :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_EXAMPLE')"
              :placeholder="
                $t(
                  'WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.BUTTON_EXAMPLE_PLACEHOLDER'
                )
              "
            />
          </div>
        </section>
      </div>

      <aside class="flex flex-col gap-3 rounded-xl bg-n-alpha-1 p-4">
        <div>
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.DESCRIPTION') }}
          </p>
        </div>
        <div
          class="flex min-h-64 items-center justify-center overflow-hidden rounded-lg bg-n-alpha-2 p-3"
        >
          <TemplatePreview
            :template="previewTemplate"
            :variables="previewVariables"
            :platform="PLATFORMS.WHATSAPP"
          />
        </div>
        <p v-if="hasNonTextHeader" class="text-xs text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.MEDIA_NOTE') }}
        </p>
        <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-xs">
          <dt class="text-n-slate-10">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.NAME') }}
          </dt>
          <dd class="truncate text-n-slate-12">
            {{
              state.name ||
              $t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.EMPTY_VALUE')
            }}
          </dd>
          <dt class="text-n-slate-10">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.LANGUAGE') }}
          </dt>
          <dd class="truncate text-n-slate-12">
            {{
              state.language
                ? formatTemplateLanguage(state.language)
                : $t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.EMPTY_VALUE')
            }}
          </dd>
          <dt class="text-n-slate-10">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.CREATE.FIELDS.CATEGORY') }}
          </dt>
          <dd class="truncate text-n-slate-12">
            {{
              categoryOptions.find(option => option.value === state.category)
                ?.label ||
              $t('WHATSAPP_TEMPLATE_MGMT.CREATE.PREVIEW.EMPTY_VALUE')
            }}
          </dd>
        </dl>
      </aside>
    </div>

    <template #footer>
      <div class="flex items-center justify-end w-full gap-3">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.CANCEL')"
          :disabled="isSubmitting"
          @click="closeDialog"
        />
        <Button
          type="submit"
          :label="$t('WHATSAPP_TEMPLATE_MGMT.CREATE.BUTTONS.SUBMIT')"
          :is-loading="isSubmitting"
          :disabled="isSubmitDisabled"
        />
      </div>
    </template>
  </Dialog>
</template>
